import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Megaphone, ArrowLeft, Check, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const plans = [
  { id: 'basic', days: 7, price: 49, label_it: 'Base', label_en: 'Basic', desc_it: '7 giorni di visibilità', desc_en: '7 days visibility' },
  { id: 'standard', days: 30, price: 149, label_it: 'Standard', label_en: 'Standard', desc_it: '30 giorni di visibilità', desc_en: '30 days visibility' },
  { id: 'premium', days: 90, price: 349, label_it: 'Premium', label_en: 'Premium', desc_it: '90 giorni + priorità nel feed', desc_en: '90 days + feed priority' },
];

const Advertise = () => {
  const { isAuthenticated, token, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('standard');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    company_name: user?.company_name || user?.name || '',
    title: '',
    description: '',
    link_url: '',
    image_url: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) { navigate('/login'); return; }
    if (!form.title || !form.link_url) {
      toast.error(language === 'it' ? 'Compila titolo e link' : 'Fill in title and link');
      return;
    }
    setLoading(true);
    try {
      const plan = plans.find(p => p.id === selectedPlan);
      await axios.post(`${API_URL}/api/banners/request`, {
        ...form,
        budget: plan.price,
        duration_days: plan.days,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitted(true);
      toast.success(language === 'it' ? 'Richiesta inviata!' : 'Request sent!');
    } catch {
      toast.error(language === 'it' ? 'Errore nell\'invio' : 'Error sending request');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-100 overflow-y-auto">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-emerald-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-stone-900 mb-4">
              {language === 'it' ? 'Richiesta Inviata!' : 'Request Sent!'}
            </h1>
            <p className="text-stone-600 mb-8">
              {language === 'it' 
                ? 'Il nostro team ti contatterà entro 24 ore per finalizzare il tuo banner pubblicitario.' 
                : 'Our team will contact you within 24 hours to finalize your advertising banner.'}
            </p>
            <Link to="/feed">
              <Button className="bg-emerald-800 hover:bg-emerald-900 text-white rounded-full px-8">
                {language === 'it' ? 'Torna al feed' : 'Back to feed'}
              </Button>
            </Link>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20">
        <Link to="/feed" className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6">
          <ArrowLeft className="h-5 w-5 mr-2" />
          {language === 'it' ? 'Torna al feed' : 'Back to feed'}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-800 to-emerald-900 rounded-2xl p-8 mb-8 text-white">
            <div className="flex items-center gap-3 mb-4">
              <Megaphone className="h-8 w-8" />
              <h1 className="font-heading text-3xl font-bold">
                {language === 'it' ? 'Pubblicità su Moneyfor' : 'Advertise on Moneyfor'}
              </h1>
            </div>
            <p className="text-emerald-100 max-w-2xl">
              {language === 'it' 
                ? 'Raggiungi migliaia di utenti solidali. Promuovi la tua azienda supportando la community.' 
                : 'Reach thousands of engaged users. Promote your company while supporting the community.'}
            </p>
          </div>

          {/* Plans */}
          <h2 className="font-heading text-xl font-semibold text-stone-900 mb-4">
            {language === 'it' ? 'Scegli il tuo piano' : 'Choose your plan'}
          </h2>
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {plans.map((plan) => (
              <button key={plan.id} data-testid={`plan-${plan.id}`}
                onClick={() => setSelectedPlan(plan.id)}
                className={`p-6 rounded-2xl border-2 text-left transition-all ${
                  selectedPlan === plan.id
                    ? 'border-emerald-600 bg-emerald-50 shadow-md' : 'border-stone-200 bg-white hover:border-stone-300'
                }`}
              >
                <h3 className="font-heading text-lg font-bold text-stone-900">
                  {language === 'it' ? plan.label_it : plan.label_en}
                </h3>
                <p className="text-3xl font-bold text-emerald-800 my-2">{plan.price}€</p>
                <p className="text-sm text-stone-600">{language === 'it' ? plan.desc_it : plan.desc_en}</p>
              </button>
            ))}
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
            <h2 className="font-heading text-xl font-semibold text-stone-900 mb-6">
              {language === 'it' ? 'Dettagli del banner' : 'Banner details'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'it' ? 'Nome azienda' : 'Company name'}</Label>
                  <Input data-testid="ad-company-name" value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    className="rounded-xl" required
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'it' ? 'Titolo del banner' : 'Banner title'}</Label>
                  <Input data-testid="ad-title" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder={language === 'it' ? 'es. Scopri la nostra offerta' : 'e.g. Discover our offer'}
                    className="rounded-xl" required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{language === 'it' ? 'Descrizione' : 'Description'}</Label>
                <Textarea data-testid="ad-description" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder={language === 'it' ? 'Breve descrizione del tuo banner...' : 'Short description of your banner...'}
                  className="rounded-xl resize-none" rows={2}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'it' ? 'Link di destinazione (URL)' : 'Destination link (URL)'}</Label>
                  <Input data-testid="ad-link-url" type="url" value={form.link_url}
                    onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                    placeholder="https://www.example.com"
                    className="rounded-xl" required
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'it' ? 'URL immagine (opzionale)' : 'Image URL (optional)'}</Label>
                  <Input data-testid="ad-image-url" type="url" value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="https://www.example.com/image.jpg"
                    className="rounded-xl"
                  />
                </div>
              </div>
              <Button data-testid="submit-ad-request" type="submit" disabled={loading}
                className="w-full h-12 bg-emerald-800 hover:bg-emerald-900 text-white rounded-xl font-semibold mt-4"
              >
                {loading ? '...' : (language === 'it' ? 'Invia richiesta' : 'Submit request')}
              </Button>
            </form>
          </div>
        </motion.div>
      </main>
      <Footer />
    </div>
  );
};

export default Advertise;
